import type { Driver, HomeyAPI, ItemProps } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type DriverProps = ItemProps<Driver>;

type DriversData = { [key: string]: Driver };
type DriversById = { [key: string]: DriverProps };

interface State {
  data: DriversData | null;
  byId: DriversById | null;
  isLoading: boolean;
  error: unknown;
}

export type DriversStoreState = State;

export class DriversStore extends BaseApiStore<State, Driver> {
  constructor() {
    super('drivers');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.drivers });

      if (abortController.signal.aborted) return;

      const driversPromise = api.drivers.getDrivers({ $cache: cache });

      const [driversData] = await Promise.all([driversPromise]);

      if (abortController.signal.aborted) return;

      const drivers = Object.values(driversData);

      const data: DriversData = {};
      const byId: DriversById = {};

      for (const driverInstance of drivers) {
        const driverReference = driverInstance.getProps();

        data[driverReference.id] = driverInstance;
        byId[driverReference.id] = driverReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.drivers.on('driver.create', this.handleCreate);
    api.drivers.on('driver.update', this.handleUpdate);
    api.drivers.on('driver.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.drivers.off('driver.create', this.handleCreate);
    api.drivers.off('driver.update', this.handleUpdate);
    api.drivers.off('driver.delete', this.handleDelete);
  }

  getInstanceFromCache(driver: { id: string }) {
    return this.getCache()[driver.id];
  }

  getCache(): Readonly<Record<string, Driver>> {
    const api = this.getApi();

    return api.drivers.getCachedItems('driver');
  }

  handleCreate = (createdDriver: Driver) => {
    this.DEBUG && console.info(`create:driver:${createdDriver.id}`);
    const state = this.get();
    const driverInstance = this.getInstanceFromCache(createdDriver);

    if (driverInstance == null) return;

    const driverReference = driverInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdDriver.id]: driverInstance,
      },
      byId: {
        ...state.byId,
        [createdDriver.id]: driverReference,
      },
    });
  };

  handleUpdate = (updatedDriver: Driver) => {
    this.DEBUG && console.info(`update:driver:${updatedDriver.id}`);

    this.addToQueue(updatedDriver.id, updatedDriver);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedDriver] of Object.entries(updateQueue)) {
        const driverInstance = this.getInstanceFromCache(queuedDriver);

        if (state.data == null) {
          this.DEBUG && console.info(`update:driver:missing-data`);
          continue;
        }

        if (driverInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:driver:unknown:${id}`);
          continue;
        }

        const driverReference = driverInstance.getProps();

        nextById[id] = driverReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:driver:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedDriver: Pick<Driver, 'id'>) => {
    this.DEBUG && console.info(`delete:driver:${deletedDriver.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:driver:missing-data`);
      return;
    }

    const driverInstance = state.data[deletedDriver.id];

    if (driverInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[driverInstance.id];
    delete nextById[driverInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const drivers = Object.values(cache ?? {});
    const data: DriversData = {};
    const byId: DriversById = {};

    for (const driverInstance of drivers) {
      const driverReference = driverInstance.getProps();
      data[driverReference.id] = driverInstance;
      byId[driverReference.id] = driverReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const driversStore = new DriversStore();
